// Shows where to click on the Manage apps page in Wix, after the owner gave the uninstall reason (Robert, 28.10.2026):
// the link opens Wix in a new tab, so the owner needs to know in advance what to do there. A looping animation of
// that page as it is today (arrow at the end of our app's row, the panel on the right, "Delete app", confirm) with
// the same steps as text below it. In Wix the app is listed under its App Market name, not "Ultimo Bots".
import { useTranslation } from 'react-i18next';
import './WixUninstallGuide.css';

export const WIX_APP_NAME = 'AI Chatbot & Live Chat Agent';

const Row = ({ y, children }) => (
  <g>
    <rect x="12" y={y} width="360" height="40" rx="8" fill="#fff" stroke="#e5e7eb" />
    {children || (
      <>
        <rect x="24" y={y + 10} width="20" height="20" rx="5" fill="#e5e7eb" />
        <rect x="54" y={y + 13} width="96" height="7" rx="3.5" fill="#e5e7eb" />
      </>
    )}
    <rect x="286" y={y + 10} width="50" height="20" rx="10" fill="#fff" stroke="#d1d5db" />
    {!children && (
      <path d={`M349.5 ${y + 15}l5 5-5 5`} fill="none" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </g>
);

// The "›" in a step stands for Wix's arrow button; as a font glyph it is too small to recognise.
const withArrow = (text) =>
  text.split('›').flatMap((part, i) =>
    i === 0
      ? [part]
      : [
          <span key={i} className="wu-arrow-chip" aria-hidden="true">
            <svg viewBox="0 0 10 10" width="10" height="10">
              <path d="M3.5 1.5L7 5 3.5 8.5" fill="none" stroke="#374151" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>,
          part,
        ]
  );

const WixUninstallGuide = ({ opened = false }) => {
  const { t } = useTranslation();
  const del = t('wix_uninstall_guide_delete');
  const steps = [
    t('wix_uninstall_step_1', { button: t('wix_uninstall_open_manage') }),
    t('wix_uninstall_step_2', { app: WIX_APP_NAME }),
    t('wix_uninstall_step_3', { delete: del }),
  ];

  return (
    <div className="wu-guide-wrap">
      <svg
        className="wu-guide"
        viewBox="0 0 384 184"
        role="img"
        aria-label={t('wix_uninstall_guide_alt', { app: WIX_APP_NAME, delete: del })}
      >
        <defs>
          <clipPath id="wu-frame">
            <rect x="0" y="0" width="384" height="184" rx="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#wu-frame)">
          <rect x="0" y="0" width="384" height="184" fill="#f7f8fa" />
          <text x="16" y="25" className="wu-title">{t('wix_uninstall_guide_page')}</text>
          <line x1="0" y1="38" x2="384" y2="38" stroke="#e5e7eb" />

          <g className="wu-ultimo">
            <Row y={46}>
              <rect x="24" y="56" width="20" height="20" rx="5" fill="#5616ea" />
              <text x="34" y="70.5" textAnchor="middle" className="wu-icon-letter">U</text>
              <text x="54" y="70" className="wu-name">{WIX_APP_NAME}</text>
            </Row>
            <rect className="wu-arrow-bg" x="341" y="55" width="22" height="22" rx="6" fill="#ede9fe" />
            <path d="M349.5 61l5 5-5 5" fill="none" stroke="#5616ea" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <circle className="wu-ring wu-ring-1" cx="352" cy="66" r="12" />
          </g>
          <Row y={92} />
          <Row y={138} />

          <g className="wu-panel">
            <rect x="226" y="0" width="158" height="184" fill="#fff" />
            <line x1="226" y1="0" x2="226" y2="184" stroke="#e5e7eb" />
            <rect x="238" y="12" width="22" height="22" rx="5" fill="#5616ea" />
            <text x="249" y="27.5" textAnchor="middle" className="wu-icon-letter">U</text>
            <rect x="268" y="15" width="92" height="6" rx="3" fill="#d1d5db" />
            <rect x="268" y="26" width="64" height="5" rx="2.5" fill="#e5e7eb" />
            <rect x="238" y="44" width="134" height="34" rx="6" fill="#fff7ed" />
            <rect x="248" y="53" width="70" height="6" rx="3" fill="#fde2c4" />
            <rect x="248" y="64" width="44" height="5" rx="2.5" fill="#fde2c4" />
            <rect x="238" y="92" width="40" height="6" rx="3" fill="#9ca3af" />
            <rect x="238" y="108" width="84" height="5" rx="2.5" fill="#e5e7eb" />
            <rect x="238" y="122" width="72" height="5" rx="2.5" fill="#e5e7eb" />
            <rect x="238" y="136" width="90" height="5" rx="2.5" fill="#e5e7eb" />
            <rect className="wu-del-hover" x="232" y="148" width="146" height="22" rx="5" fill="#f1ecff" />
            <path d="M239 155h9M242 155v-1.5h3v1.5M240.2 155l.6 9h5.4l.6-9" fill="none" stroke="#111827" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            <text x="254" y="163.5" className="wu-del">{del}</text>
            <circle className="wu-ring wu-ring-2" cx="285" cy="159" r="12" />
          </g>

          <g className="wu-dialog">
            <rect x="0" y="0" width="384" height="184" fill="#111827" fillOpacity="0.32" />
            <rect x="72" y="34" width="240" height="114" rx="10" fill="#fff" />
            <rect x="90" y="52" width="132" height="8" rx="4" fill="#d1d5db" />
            <rect x="90" y="70" width="204" height="24" rx="6" fill="#fff" stroke="#d1d5db" />
            <rect x="102" y="79" width="72" height="6" rx="3" fill="#e5e7eb" />
            <path d="M276 80l4 4 4-4" fill="none" stroke="#6b7280" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <rect className="wu-confirm" x="180" y="108" width="114" height="26" rx="6" />
            <text x="237" y="125" textAnchor="middle" className="wu-confirm-label">{del}</text>
            <circle className="wu-ring wu-ring-3" cx="237" cy="121" r="13" />
          </g>

          <g className="wu-cursor">
            <path d="M0 0v17l4.6-4.2 3 6.8 2.8-1.2-3-6.8h6z" fill="#fff" stroke="#111827" strokeWidth="1.3" strokeLinejoin="round" />
          </g>
        </g>
        <rect x="0.5" y="0.5" width="383" height="183" rx="10" fill="none" stroke="#e5e7eb" />
      </svg>

      <ol className="wu-steps">
        {steps.map((text, i) => (
          <li key={i} className={`wu-step wu-step-${i + 1}`}>
            <span className={`wu-step-num${i === 0 && opened ? ' wu-step-num-done' : ''}`}>
              {i === 0 && opened ? '✓' : i + 1}
            </span>
            <span>{withArrow(text)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default WixUninstallGuide;
